import { Injectable } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import {
  BfxBlotterAction,
  ContextMenuItemState,
  SystemConfigPromiseService,
  TraderGroupPromiseLookupService,
  ActiveUserProfilePromiseService,
  ActiveUser,
  BaseLookupService,
  OpsIntPromiseLookupService,
  Roles,
} from "shared/cubiq-services";

import { BfxBaseBlotter } from "../../bfx-base-blotter/bfx-base-blotter";

import {
  BfxBaseBlotterContextMenuService,
  BfxBlotterActionEvent,
  BfxBlotterActionSubject,
  BfxLadderBlotterContextMenuEventType,
} from "../../bfx-base-blotter/bfx-base-blotter-contextmenu.service";

import { BfxBlotterOpsIntContextMenuItem } from "../models/contextmenu/BfxBlotterOpsIntContextMenuItem";
import { BfxBlotterOpsIntContextMenuState } from "../models/contextmenu/BfxBlotterOpsIntContextMenuState";
import { BfxBlotterOpsIntContextMenuEvent } from "../models/contextmenu/BfxBlotterOpsIntContextMenuEvent";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

export const RESEND_ENABLED: Set<CubiqTS.OpsExceptionType> = new Set([
  CubiqTS.OpsExceptionType.NO_ACK_RECEIVED,
  CubiqTS.OpsExceptionType.FAILED_SENDING,
  CubiqTS.OpsExceptionType.NACK_RECEIVED,
  CubiqTS.OpsExceptionType.FAILED_TO_BOOK,
  CubiqTS.OpsExceptionType.BOOKING_ERROR,
]);

export const REPRICE_ENABLED: Set<CubiqTS.OpsExceptionType> = new Set([
  CubiqTS.OpsExceptionType.FAILED_TO_PRICE,
  CubiqTS.OpsExceptionType.FAILED_TO_TRIGGER,
]);

export const CLEAR_DIALOG_ENABLED: Set<CubiqTS.OpsExceptionType> = new Set([
  CubiqTS.OpsExceptionType.SWEPT_PAST_ROLLOVER,
  CubiqTS.OpsExceptionType.NO_ACK_RECEIVED,
  CubiqTS.OpsExceptionType.NACK_RECEIVED,
  CubiqTS.OpsExceptionType.NACK_RECEIVED_CLEAR_ONLY,
  CubiqTS.OpsExceptionType.FAILED_TO_BOOK,
  CubiqTS.OpsExceptionType.BOOKING_ERROR,
  CubiqTS.OpsExceptionType.FAILED_SENDING,
  CubiqTS.OpsExceptionType.FAILED_TO_PRICE,
  CubiqTS.OpsExceptionType.FAILED_TO_TRIGGER,
  CubiqTS.OpsExceptionType.FAILED_TO_TRIGGER_LIMIT,
]);

export const BMARK_EVENTS_FOR_CLEARING: Set<CubiqTS.OpsExceptionType> = new Set([
  CubiqTS.OpsExceptionType.FAILED_TO_PRICE,
  CubiqTS.OpsExceptionType.FAILED_TO_TRIGGER,
  CubiqTS.OpsExceptionType.SWEPT_PAST_ROLLOVER,
  CubiqTS.OpsExceptionType.ENTRY_PRICING,
]);

export const STP_EVENTS_FOR_CLEARING: Set<CubiqTS.OpsExceptionType> = new Set([
  CubiqTS.OpsExceptionType.NO_ACK_RECEIVED,
  CubiqTS.OpsExceptionType.NACK_RECEIVED,
  CubiqTS.OpsExceptionType.NACK_RECEIVED_CLEAR_ONLY,
  CubiqTS.OpsExceptionType.FAILED_TO_BOOK,
  CubiqTS.OpsExceptionType.BOOKING_ERROR,
  CubiqTS.OpsExceptionType.FAILED_SENDING,
]);

export const CANCEL_ORDER_ENABLED: Set<CubiqTS.OpsExceptionType> = new Set([
  CubiqTS.OpsExceptionType.FAILED_TO_PRICE,
  CubiqTS.OpsExceptionType.FAILED_TO_TRIGGER,
  CubiqTS.OpsExceptionType.SWEPT_PAST_ROLLOVER,
]);

@Injectable()
export class BfxBlotterOpsIntContextMenuService extends BfxBaseBlotterContextMenuService<any> {
  public menuItems: Array<BfxBlotterOpsIntContextMenuItem> = [];
  protected menuState = new BfxBlotterOpsIntContextMenuState();
  private activeUser: ActiveUser;

  private static isItemOptIntEvent(item: CubiqTS.OpsIntEventImpl): boolean {
    return item != null && item instanceof CubiqTS.OpsIntEventImpl;
  }

  private static hasOpsExcInfo(item: CubiqTS.OpsIntEventImpl): boolean {
    return (
      BfxBlotterOpsIntContextMenuService.isItemOptIntEvent(item) &&
      item.info != null &&
      item.info instanceof CubiqTS.OpsExcInfoImpl
    );
  }

  private static hasOpsExcRecord(item: CubiqTS.OpsIntEventImpl): boolean {
    return BfxBlotterOpsIntContextMenuService.hasOpsExcInfo(item) && item.info.opsException != null;
  }

  private static hasOrderData(item: CubiqTS.OpsIntEventImpl) {
    return (
      BfxBlotterOpsIntContextMenuService.hasOpsExcInfo(item) &&
      item.info.orderData != null &&
      item.info.orderData instanceof CubiqTS.OrderDataImpl &&
      CubiqTS.Long.isLong(item.info.orderData.orderId)
    );
  }

  private static hasDealData(item: CubiqTS.OpsIntEventImpl) {
    return (
      BfxBlotterOpsIntContextMenuService.hasOpsExcInfo(item) &&
      item.info.deal != null &&
      item.info.deal instanceof CubiqTS.DealImpl &&
      CubiqTS.Long.isLong(item.info.deal.id)
    );
  }

  constructor(
    protected opsIntLookupService: OpsIntPromiseLookupService,
    protected systemConfigService: SystemConfigPromiseService,
    protected traderGroupService: TraderGroupPromiseLookupService,
    protected activeUserService: ActiveUserProfilePromiseService,
  ) {
    super(new BfxBlotterActionSubject());

    Promise.all([
      this.activeUserService.getActiveUserData(),
      this.systemConfigService.hasRole(Roles.DI_OPERATIONS_RESUBMIT),
      this.systemConfigService.hasRole(Roles.DI_OPERATIONS_CANCEL),
      this.systemConfigService.hasRole(Roles.ROLE_DI_OPERATIONS_CLEAR),
    ]).then(
      ([activeUser, hasOperationsResubmit, hasOperationsCancel, hasOperationsClear]) => {
        this.activeUser = activeUser;
        this.menuState.isTrader = CubiqTS.UserType.TRADER === activeUser.userTypeId;
        this.menuState.isSales = CubiqTS.UserType.SALES === activeUser.userTypeId;
        this.menuState.isClient = CubiqTS.UserType.CLIENT === activeUser.userTypeId;
        this.menuState.hasOperationsResubmit = hasOperationsResubmit;
        this.menuState.hasOperationsClear = hasOperationsClear;
        this.menuState.hasOperationsCancel = hasOperationsCancel;
      },
      (reason) => {
        logger.out.error(`BfxBlotterOpsIntContextMenuService: promise failure ${reason}`);
      },
    );
  }

  public getMenuItems(): Array<BfxBlotterOpsIntContextMenuItem> {
    return this.menuItems;
  }

  protected prepareActions(ladderBlotter: BfxBaseBlotter): void {
    // nothing to add
  }

  public buildMenu(
    blotter: any,
    pageX: number,
    pageY: number,
    selectedIds: Array<string>,
    selectedItems?: Array<CubiqTS.OpsIntEventImpl> | null,
  ): void {
    this.menuState.reset();

    let base = this.opsIntLookupService.getBase() as BaseLookupService<
      string,
      CubiqTS.OpsIntEventImpl
    >;
    if (selectedItems == null) {
      selectedItems = [];
      for (let i = 0; i < selectedIds.length; i++) {
        let item = base.getItem(selectedIds[i]);

        if (item instanceof CubiqTS.OpsIntEventImpl) {
          selectedItems.push(item);
        } else {
          selectedItems.push(null);
        }
      }
      this.setSelectedItems(selectedItems, blotter);
    }

    let menuItems = this.createMenuItems();
    if (Array.isArray(selectedItems) && selectedItems.length > 0) {
      this.menuState.count = selectedItems.length;

      for (let selectedItem of selectedItems) {
        for (let [actionId, menuItem] of menuItems) {
          switch (actionId) {
            case BfxBlotterAction.ACTION_OPS_INT_CANCEL_ORDER: {
              let cancelAvailable = true;
              let cancelEnabled = this.checkCancelOrderEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, cancelEnabled);
              this.setMenuItemAvailableEnabled(menuItem, cancelAvailable, cancelEnabled);
              break;
            }
            case BfxBlotterAction.ACTION_OPS_INT_RESEND: {
              let resendAvailable = true;
              let resendEnabled = this.checkResendEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, resendEnabled);
              this.setMenuItemAvailableEnabled(menuItem, resendAvailable, resendEnabled);
              break;
            }
            case BfxBlotterAction.ACTION_OPS_INT_VIEW_DETAILS: {
              let viewDetailsAvailable = true;
              let viewDetailsEnabled = this.checkViewDetailsEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, viewDetailsEnabled);
              this.setMenuItemAvailableEnabled(menuItem, viewDetailsAvailable, viewDetailsEnabled);
              break;
            }
            case BfxBlotterAction.ACTION_OPS_INT_REPRICE: {
              let rePriceAvailable = true;
              let rePriceEnabled = this.checkRePriceEnabled(selectedItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, rePriceEnabled);
              this.setMenuItemAvailableEnabled(menuItem, rePriceAvailable, rePriceEnabled);
              break;
            }
            case BfxBlotterAction.ACTION_OPS_INT_CLEAR: {
              let clearAvailable = true;
              this.addReferencedOpsExceptionIdToMenuItem(menuItem, selectedItem);
              let clearEnabled = this.checkClearEnabled(selectedItem, menuItem);
              let isDialogOpening = this.checkClearDialogEnabled(selectedItem, menuItem);
              this.addReferenceIdToMenuItem(menuItem, selectedItem, clearEnabled, isDialogOpening);
              this.setMenuItemAvailableEnabled(menuItem, clearAvailable, clearEnabled);
              break;
            }
            default:
              logger.out.warn(
                `${this.constructor.name}.doMenu() Invalid action for %O %O`,
                selectedItem,
                menuItem,
              );
              break;
          }
        }
      }
    }

    this.menuItems = Array.from(menuItems.values());

    this.showMenuSubject.next(
      new BfxBlotterOpsIntContextMenuEvent(
        BfxLadderBlotterContextMenuEventType.MENU_SHOW,
        blotter,
        pageX,
        pageY,
        [],
        null,
        Array.from(menuItems.values()),
      ),
    );
  }

  public fireActionEvent(
    actionId: BfxBlotterAction,
    ladderBlotter: any,
    selectedIds?: Array<string>,
    selectedItems?: Array<any>,
    itemState?: ContextMenuItemState,
    hide?: boolean,
    menuBoundingClientRect?: any,
    actionEvent?: any,
  ): void {
    let event: BfxBlotterActionEvent = new BfxBlotterActionEvent(
      actionId,
      ladderBlotter,
      this.getSelectedIds(selectedIds, ladderBlotter),
      this.getSelectedItems(selectedItems, ladderBlotter),
      itemState,
      hide,
      menuBoundingClientRect,
      actionEvent,
    );
    this.actionSubject.next(event);
  }

  protected applySelectedItemToMenuItem(
    menuItem: BfxBlotterOpsIntContextMenuItem,
    itemAvailable: boolean,
    itemEnabled: boolean,
  ): void {
    menuItem.state.available =
      menuItem.state.available == null ? itemAvailable : menuItem.state.available || itemAvailable;
    menuItem.state.enabled =
      menuItem.state.enabled == null ? itemEnabled : menuItem.state.enabled || itemEnabled;
  }

  protected setMenuItemAvailableEnabled(
    menuItem: BfxBlotterOpsIntContextMenuItem,
    itemAvailable: boolean,
    itemEnabled: boolean,
  ): void {
    menuItem.state.available = itemAvailable;
    menuItem.state.enabled = itemEnabled;
  }

  protected addReferenceIdToMenuItem(
    menuItem: BfxBlotterOpsIntContextMenuItem,
    item: CubiqTS.OpsIntEventImpl,
    itemEnabled: boolean,
    isDialogOpening = false,
  ): void {
    if (
      menuItem instanceof BfxBlotterOpsIntContextMenuItem &&
      item instanceof CubiqTS.OpsIntEventImpl &&
      typeof itemEnabled === "boolean" &&
      itemEnabled
    ) {
      let dealId =
        item.info instanceof CubiqTS.OpsExcInfoImpl &&
        item.info.deal instanceof CubiqTS.DealImpl &&
        CubiqTS.Long.isLong(item.info.deal.id)
          ? item.info.deal.id.toString()
          : null;
      let orderId = null;

      if (dealId == null) {
        orderId =
          item.info instanceof CubiqTS.OpsExcInfoImpl &&
          item.info.orderData instanceof CubiqTS.OrderDataImpl &&
          CubiqTS.Long.isLong(item.info.orderData.orderId)
            ? item.info.orderData.orderId.toString()
            : null;
      }

      if (dealId != null) {
        menuItem.state.referencedDealIds.push(dealId);
        if (isDialogOpening) {
          menuItem.state.clearDialogReferencedIds.set(item.eventId.toString(), dealId);
        }
      } else if (orderId != null) {
        menuItem.state.referencedOrderIds.push(orderId);
        if (isDialogOpening) {
          menuItem.state.clearDialogReferencedIds.set(item.eventId.toString(), orderId);
        }
      }
      menuItem.state.referencedOpsIntIds.push(item.eventId.toString());
    }
  }

  /**
   * Extracted from `addReferenceIdToMenuItem` in order to be invoked before checking if clear is enabled.
   * OpsExceptionIds list must be updated in order not to be one event behind the clear check.
   */
  private addReferencedOpsExceptionIdToMenuItem(
    menuItem: BfxBlotterOpsIntContextMenuItem,
    item: CubiqTS.OpsIntEventImpl,
  ) {
    let opsExceptionId = BfxBlotterOpsIntContextMenuService.hasOpsExcInfo(item)
      ? item.info.opsException.exceptionTypeId
      : null;

    if (opsExceptionId != null) {
      menuItem.state.referencedOpsExceptionId.push(opsExceptionId);
    }
  }

  protected createMenuItems(): Map<BfxBlotterAction, BfxBlotterOpsIntContextMenuItem> {
    let menuItems: Map<BfxBlotterAction, BfxBlotterOpsIntContextMenuItem> = new Map();
    menuItems.set(
      BfxBlotterAction.ACTION_OPS_INT_CANCEL_ORDER,
      new BfxBlotterOpsIntContextMenuItem(BfxBlotterAction.ACTION_OPS_INT_CANCEL_ORDER, "Cancel"),
    );
    menuItems.set(
      BfxBlotterAction.ACTION_OPS_INT_CLEAR,
      new BfxBlotterOpsIntContextMenuItem(BfxBlotterAction.ACTION_OPS_INT_CLEAR, "Clear"),
    );
    menuItems.set(
      BfxBlotterAction.ACTION_OPS_INT_REPRICE,
      new BfxBlotterOpsIntContextMenuItem(BfxBlotterAction.ACTION_OPS_INT_REPRICE, "Re-price"),
    );
    menuItems.set(
      BfxBlotterAction.ACTION_OPS_INT_RESEND,
      new BfxBlotterOpsIntContextMenuItem(BfxBlotterAction.ACTION_OPS_INT_RESEND, "Re-send"),
    );
    menuItems.set(
      BfxBlotterAction.ACTION_OPS_INT_VIEW_DETAILS,
      new BfxBlotterOpsIntContextMenuItem(
        BfxBlotterAction.ACTION_OPS_INT_VIEW_DETAILS,
        "View Details",
      ),
    );
    return menuItems;
  }

  protected checkCancelOrderEnabled(item: CubiqTS.OpsIntEventImpl): boolean {
    let enabled =
      this.menuState.hasOperationsCancel === true &&
      this.menuState.cancelOrderIneligible === false &&
      item != null &&
      item instanceof CubiqTS.OpsIntEventImpl &&
      item.info != null &&
      item.info instanceof CubiqTS.OpsExcInfoImpl &&
      item.info.orderData != null &&
      item.info.orderData instanceof CubiqTS.OrderDataImpl &&
      CubiqTS.Long.isLong(item.info.orderData.orderId) &&
      item.info != null &&
      item.info.opsException != null &&
      CANCEL_ORDER_ENABLED.has(item.info.opsException.exceptionTypeId);

    if (enabled === false) {
      // a single ineligble item disables resend for entire selected set.
      this.menuState.cancelOrderIneligible = true;
    }

    return enabled;
  }

  protected checkResendEnabled(item: CubiqTS.OpsIntEventImpl): boolean {
    let enabled =
      this.menuState.hasOperationsResubmit === true &&
      this.menuState.resendIneligible === false &&
      item != null &&
      item instanceof CubiqTS.OpsIntEventImpl &&
      item.info != null &&
      item.info instanceof CubiqTS.OpsExcInfoImpl &&
      item.info.deal != null &&
      item.info.deal instanceof CubiqTS.DealImpl &&
      CubiqTS.Long.isLong(item.info.deal.id) &&
      item.info != null &&
      item.info.opsException != null &&
      RESEND_ENABLED.has(item.info.opsException.exceptionTypeId);

    if (enabled === false) {
      // a single ineligble item disables resend for entire selected set.
      this.menuState.resendIneligible = true;
    }

    return enabled;
  }

  protected checkViewDetailsEnabled(item: CubiqTS.OpsIntEventImpl): boolean {
    return (
      this.menuState.count === 1 &&
      item != null &&
      item instanceof CubiqTS.OpsIntEventImpl &&
      ((item.info != null &&
        item.info instanceof CubiqTS.OpsExcInfoImpl &&
        item.info.deal != null &&
        item.info.deal instanceof CubiqTS.DealImpl &&
        CubiqTS.Long.isLong(item.info.deal.id)) ||
        (item.info != null &&
          item.info instanceof CubiqTS.OpsExcInfoImpl &&
          item.info.orderData != null &&
          item.info.orderData instanceof CubiqTS.OrderDataImpl &&
          CubiqTS.Long.isLong(item.info.orderData.orderId)))
    );
  }

  protected checkRePriceEnabled(item: CubiqTS.OpsIntEventImpl): boolean {
    let rePriceEnabled =
      BfxBlotterOpsIntContextMenuService.hasOrderData(item) &&
      this.menuState.hasOperationsResubmit == true &&
      this.menuState.rePriceIneligible == false &&
      item?.info?.orderData?.orderType != CubiqTS.OrderType.BMALGO &&
      (item?.info?.orderData?.orderType != CubiqTS.OrderType.BENCHMARK ||
        !this.isExternallyOwned(item)) &&
      BfxBlotterOpsIntContextMenuService.hasOpsExcRecord(item) &&
      REPRICE_ENABLED.has(item.info.opsException.exceptionTypeId);

    if (rePriceEnabled == false) {
      this.menuState.rePriceIneligible = true;
    }
    return rePriceEnabled;
  }

  protected isExternallyOwned(item: CubiqTS.OpsIntEventImpl): boolean {
    let external: boolean = false;

    try {
      let deskId: CubiqTS.Long = item?.info?.orderData?.deskId;

      if (deskId != null) {
        let traderGroup: CubiqTS.TradingGroupImpl = null;
        traderGroup = this.traderGroupService?.getBase()?.getTraderGroup(deskId);
        external = traderGroup != null && traderGroup.isExternallyFilledFg;
      }
    } catch (e) {
      logger.out.warn(
        `Failed to determine if ops event order is externally owned` +
          ` ${item?.info?.orderData?.orderId?.toString()}: ${e}`,
      );
    }

    return external;
  }

  protected checkClearEnabled(
    item: CubiqTS.OpsIntEventImpl,
    menuItem: BfxBlotterOpsIntContextMenuItem,
  ): boolean {
    const selectionHasPristineBmarkCategory: boolean =
      menuItem.state.referencedOpsExceptionId.every((exceptionId) =>
        BMARK_EVENTS_FOR_CLEARING.has(exceptionId),
      );
    const selectionHasPristineStp: boolean = menuItem.state.referencedOpsExceptionId.every(
      (exceptionId) => STP_EVENTS_FOR_CLEARING.has(exceptionId),
    );

    const selectionHasPristineOtherCategories: boolean =
      menuItem.state.referencedOpsExceptionId.every(
        (exceptionId) =>
          !BMARK_EVENTS_FOR_CLEARING.has(exceptionId) && !STP_EVENTS_FOR_CLEARING.has(exceptionId),
      );

    const selectionHasStpOrBmarkCategory: boolean = menuItem.state.referencedOpsExceptionId.every(
      (exceptionId) =>
        BMARK_EVENTS_FOR_CLEARING.has(exceptionId) || STP_EVENTS_FOR_CLEARING.has(exceptionId),
    );

    let clearEnabled: boolean =
      this.menuState.hasOperationsClear &&
      (selectionHasPristineOtherCategories ||
        selectionHasPristineStp ||
        selectionHasPristineBmarkCategory);

    if (clearEnabled == false) {
      this.menuState.isClearIneligible = true;
    }
    return clearEnabled;
  }

  protected checkClearDialogEnabled(
    item: CubiqTS.OpsIntEventImpl,
    menuItem: BfxBlotterOpsIntContextMenuItem,
  ): boolean {
    let clearDialogEnabled =
      (BfxBlotterOpsIntContextMenuService.hasOrderData(item) ||
        BfxBlotterOpsIntContextMenuService.hasDealData(item)) &&
      this.menuState.hasOperationsClear &&
      this.menuState.isClearIneligible == false &&
      BfxBlotterOpsIntContextMenuService.hasOpsExcRecord(item) &&
      CLEAR_DIALOG_ENABLED.has(item.info.opsException.exceptionTypeId);

    return clearDialogEnabled;
  }

  protected getEnabledActions(
    quotes: Array<any>,
    selectedOrderIds: Array<string>,
  ): Array<BfxBlotterAction> {
    return [];
  }
}
